/*eslint @typescript-eslint/no-unused-vars: 'off'*/
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, RemoveCircle as Unsubscribe } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PhoneNumber from 'awesome-phonenumber';
import { FC, useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate, useOutletContext, useRouteLoaderData } from 'react-router-dom';
import { ConfirmDialog } from './components/ConfirmDialog';
// import { AddPatientRecipient, CommunicationTypeFieldNames, EditPatientRecipient, PatientRecipientFormData } from './components/PatientRecipientForm';
import { ProfOutletContext } from './Prof';
import { deleteRecipient, ProfileData, unsubscribeAllRecipients } from './profiles';
import { StyledBaseTableCell, StyledTableCell, StyledTableRow } from './ProfIndex';
import { TelecomOptionInterface } from './types';

export const ProfRecipientsOutlet: FC = () => {
  const context = useOutletContext<ProfOutletContext>();
  return (
    <div id='recipients'>
      <Outlet {...{ context }} />
    </div>
  );
}

export const ProfRecipients: FC = () => {
  const data = useRouteLoaderData( 'prof' ) as ProfileData;
  const { recipient, recipients, countryCode, profileId, organization } = data || {};
  const isXSmall = false;
  const navigate = useNavigate();

  const setIsLoaded = ( _v = false ): void => navigate( '.' ); // mimic reloading page data

  const [ openDeleteRecipient, setOpenDeleteRecipient ] = useState<string>( '' );
  const [ openUnsubscribeAllRecipients, setOpenUnsubscribeAllRecipients ] = useState( false );
  const [ deletedIds, setDeletedIds ] = useState<string[]>( [] ); // both contact methods and recipients

  const getFormattedValueOfTelecomOption = useCallback( ( telecomOption?: TelecomOptionInterface ): string => {
    const { system, value = '' } = telecomOption || {};
    if( system != 'phone' ) return value;
    return value && PhoneNumber( value, countryCode ).getNumber( 'national' ) || '';
  }, [] );

  const onDeleteRecipient = useCallback( async ( id: string ) => {
    await deleteRecipient( profileId, id );
    setIsLoaded( false );
  }, [ profileId ] );

  const onUnsubscribeAllRecipients = useCallback( async () => {
    await unsubscribeAllRecipients( profileId );
    setIsLoaded( false );
  }, [ profileId ] );


  // Fix 'Error: Hydration failed because the initial UI does not match what was rendered on the server'
  const [ mounted, setMounted ] = useState( false );
  useEffect( () => setMounted( true ), [] );
  if( !mounted ) return null;

  if( !organization || !recipient ) return null;

  return (
    <Grid item container key='content' style={{ padding: isXSmall ? '1.5rem' : undefined }} >

      <Grid item xs={12}>
        <Typography variant='h6' align='left'
          sx={{
            color: 'black',
            margin: 0,
            padding: 0,
            textTransform: 'uppercase',
            width: isXSmall ? '100%' : undefined,
          }}
        >
          authorized recipients {recipients.length > 0 ? `(${ recipients.length })` : ''}
        </Typography>

        <TableContainer sx={{ width: isXSmall ? undefined : 'fit-content' }}>
          <Table size='small' padding='none' aria-label='authorized recipients table'
          >
            <TableHead sx={{ display: 'none' }}>
              <StyledTableRow>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell align='left'>Value</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {recipients.map( ( row, idx ) => {
                if( row._id && deletedIds.includes( row._id.toString() ) ) return;

                return (
                  <StyledTableRow
                    key={`row${ idx }`}
                    hover={true}
                    onClick={() => { navigate( row._id.toString() ) }}
                  >

                    <StyledTableCell scope='row'>
                      {row.fullName}
                    </StyledTableCell>


                    <StyledTableCell scope='row' >
                      {getFormattedValueOfTelecomOption( row.telecomOptions[ 0 ] )
                      } {
                        getFormattedValueOfTelecomOption( row.telecomOptions[ 1 ] )}
                    </StyledTableCell>

                    <StyledBaseTableCell align='right'>
                      <Tooltip title='Delete' placement='left'>
                        <IconButton aria-label='delete'
                          onClick={( e ) => {
                            e.stopPropagation();
                            setOpenDeleteRecipient( row._id.toString() )
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>

                      <ConfirmDialog
                        open={openDeleteRecipient == row._id.toString()}
                        title={'Delete ' + ( row.fullName ? `'${ row.fullName }'` : 'recipient' )}
                        message='Are you sure you want to delete this recipient ?'
                        confirmButton='Delete'
                        onClose={( confirmed ) => {
                          if( confirmed && row._id ) {
                            onDeleteRecipient( row._id.toString() );
                            setDeletedIds( [ ...deletedIds, row._id.toString() ] );
                          }
                          setOpenDeleteRecipient( '' );
                        }}
                        fullScreen={isXSmall}
                      />

                    </StyledBaseTableCell>

                    <StyledBaseTableCell scope='row'>
                      <Tooltip title='Edit' placement='right' >
                        <IconButton aria-label='edit'
                          onClick={( e ) => {
                            e.stopPropagation();
                            navigate( `${ row._id.toString() }/edit` );
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                    </StyledBaseTableCell>

                  </StyledTableRow>
                );
              } )}
            </TableBody>
          </Table>
        </TableContainer>

        <Grid item sx={{ mt: '0.5em', mb: '1em' }}>
          <Button
            onClick={() => navigate( 'add' )}
            // disabled={ index <= 0 }
            className='addButton addRecipientButton'
            startIcon={<AddIcon />}
          >
            {isXSmall ? 'Recipient' : 'Add recipient'}
          </Button>

          {recipients.length > 0 &&
            <Button
              onClick={() => setOpenUnsubscribeAllRecipients( true )}
              className='addButton unsubscribeAllButton'
              color='error'
              startIcon={!isXSmall ? <Unsubscribe /> : null}
            >
              Unsubscribe all
            </Button>
          }
          <ConfirmDialog
            open={openUnsubscribeAllRecipients}
            title={'Unsubscribe all recipients'}
            message='Are you sure you want to unsubscribe all recipients?'
            confirmButton='Unsubscribe all'
            onClose={( confirmed ) => {
              if( confirmed ) {
                onUnsubscribeAllRecipients();
              }
              setOpenUnsubscribeAllRecipients( false );
            }}
            fullScreen={isXSmall}
          />


        </Grid>


      </Grid >

    </Grid>
  )
}
